import { useCallback } from 'react';
import toast from 'react-hot-toast';

interface ApiError {
  response?: {
    data?: {
      error?: string;
      errors?: string[];
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

export const useErrorHandler = () => {
  const handleError = useCallback((error: ApiError, defaultMessage = 'Ocorreu um erro inesperado') => {
    console.error('API Error:', error);

    let errorMessage = defaultMessage;
    let hasSpecificError = false;

    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
      hasSpecificError = true;
    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      errorMessage = error.response.data.errors.join(', ');
      hasSpecificError = true;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
      hasSpecificError = true;
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Tratamento específico para códigos de status HTTP apenas se não houver mensagem específica
    if (error.response?.status && !hasSpecificError) {
      switch (error.response.status) {
        case 400:
          errorMessage = 'Dados inválidos. Verifique as informações e tente novamente.';
          break;
        case 401:
          // Não forçar redirect automático aqui para evitar "refresh" inesperado.
          // O fluxo de autenticação (AuthGuard / rotas protegidas) já lida
          // com envio do usuário para a tela de login quando necessário.
          errorMessage = 'Sessão expirada. Faça login novamente.';
          break;
        case 403:
          errorMessage = 'Você não tem permissão para realizar esta ação.';
          break;
        case 404:
          errorMessage = 'Recurso não encontrado.';
          break;
        case 409:
          errorMessage = 'Conflito de dados. O item pode já existir.';
          break;
        case 422:
          errorMessage = 'Dados inválidos. Verifique os campos obrigatórios.';
          break;
        case 500:
          errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
          break;
        case 503:
          errorMessage = 'Serviço temporariamente indisponível. Tente novamente mais tarde.';
          break;
        default:
          if (error.response.status >= 500) {
            errorMessage = 'Erro do servidor. Tente novamente mais tarde.';
          }
      }
    }

    toast.error(errorMessage);
    return errorMessage;
  }, []);

  const handleSuccess = useCallback((message: string) => {
    toast.success(message);
  }, []);

  return { handleError, handleSuccess };
};
